// =============================================================================
// app/src/lib/imagem.ts — a foto da peça: comprimir no aparelho e montar a URL.
//
// POR QUE COMPRIMIR AQUI, E NÃO ACEITAR O ARQUIVO COMO VEIO. Foto de celular
// tem 3–8 MB; no 4G do balcão isso é um upload de meio minuto por peça — o
// mutirão morreria nisso. Reencodar no canvas derruba para ~150 KB SEM perda
// visível numa foto de prateleira, e tem um efeito colateral que aqui é
// requisito: o canvas NÃO copia metadados, então o EXIF morre no aparelho —
// inclusive a coordenada GPS de onde a foto foi tirada, que jamais pode parar
// numa rota pública.
//
// SEMPRE JPEG na saída, seja qual for a entrada: é o contrato com a RPC (que
// confere os magic bytes) e com a rota do Caddy (que declara o Content-Type
// fixo). Um formato só elimina a negociação de tipo inteira.
//
// A parte que decide TAMANHO é pura e testada (dimensoesFinais, urlDaImagem);
// a parte que toca canvas/FileReader é casca fina sobre API de navegador.
// =============================================================================

/** Lado maior da foto final. 1280 px identifica peça de sobra e cabe no 4G. */
export const LADO_MAXIMO = 1280;

/** O alvo da compressão. O teto DE VERDADE é o da RPC (600 KB) — este número
 *  só guia a escolha de qualidade aqui dentro. */
export const ALVO_BYTES = 250 * 1024;

/** Espelho do teto da RPC. Estourar isto aqui vira erro legível ANTES do
 *  upload, em vez de uma recusa do servidor depois de meio minuto de 4G. */
export const TETO_BYTES = 600 * 1024;

export interface ImagemComprimida {
  /** O binário em base64 — pronto para a RPC. */
  base64: string;
  largura: number;
  altura: number;
  bytes: number;
  /** data URL para <img> de pré-visualização, sem tocar a rede. */
  previa: string;
}

/**
 * As dimensões finais: encolhe proporcionalmente até o lado maior caber em
 * `maximo`; NUNCA amplia (foto pequena ampliada é borrão que parece defeito
 * do sistema). Arredonda porque canvas não aceita pixel quebrado.
 */
export function dimensoesFinais(
  largura: number, altura: number, maximo: number = LADO_MAXIMO,
): { largura: number; altura: number } {
  if (largura <= 0 || altura <= 0) return { largura: 1, altura: 1 };
  const maior = Math.max(largura, altura);
  if (maior <= maximo) return { largura: Math.round(largura), altura: Math.round(altura) };
  const fator = maximo / maior;
  return {
    largura: Math.max(1, Math.round(largura * fator)),
    altura: Math.max(1, Math.round(altura * fator)),
  };
}

/**
 * A URL pública da foto. `?v=` é o cache-busting: a rota do Caddy manda um dia
 * de cache, e trocar a foto troca o updated_at — logo, troca a URL. Sem isso,
 * a foto nova só apareceria no dia seguinte.
 *
 * `base` vem de fora (VITE_SUPABASE_URL) para a função ser pura e testável.
 */
export function urlDaImagem(base: string, productId: string, updatedAt: string): string {
  const v = Date.parse(updatedAt);
  return `${base.replace(/\/$/, '')}/imagens/pecas/${productId}?v=${Number.isFinite(v) ? v : 0}`;
}

/** A URL de UMA foto da galeria (031) — o endereço é o id DA FOTO. */
export function urlDaFotoItem(base: string, fotoId: string, updatedAt: string): string {
  const v = Date.parse(updatedAt);
  return `${base.replace(/\/$/, '')}/imagens/foto/${fotoId}?v=${Number.isFinite(v) ? v : 0}`;
}

/** O que o seletor de arquivo aceita. `image/*` deixa o celular oferecer a
 *  câmera; aqui só se recusa o que nem imagem diz ser. */
export function pareceImagem(tipo: string): boolean {
  return tipo.startsWith('image/');
}

// --- A casca de navegador -----------------------------------------------------

/**
 * Arquivo → JPEG comprimido, com EXIF removido de brinde (canvas não copia
 * metadado). Tenta qualidades decrescentes até caber no alvo; se nem a última
 * couber no TETO, o erro é legível — e o upload nunca começa.
 */
export async function comprimirParaJpeg(arquivo: File): Promise<ImagemComprimida> {
  if (!pareceImagem(arquivo.type)) {
    throw new Error('Escolha uma imagem (foto da câmera ou arquivo de foto).');
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(arquivo);
  } catch {
    // HEIC de iPhone em navegador sem decodificador cai aqui.
    throw new Error('Não deu para ler esta foto neste aparelho. '
      + 'Tente tirar a foto direto pela câmera na tela, em vez de escolher do álbum.');
  }

  const { largura, altura } = dimensoesFinais(bitmap.width, bitmap.height);
  const tela = document.createElement('canvas');
  tela.width = largura;
  tela.height = altura;
  const ctx = tela.getContext('2d');
  if (!ctx) throw new Error('Este navegador não deixou preparar a imagem.');
  // Fundo branco: JPEG não tem transparência, e PNG transparente reencodado
  // sem isto vira fundo PRETO — foto de peça escura em fundo preto some.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, largura, altura);
  ctx.drawImage(bitmap, 0, 0, largura, altura);
  bitmap.close();

  let blob: Blob | null = null;
  for (const qualidade of [0.82, 0.7, 0.58]) {
    // eslint-disable-next-line no-await-in-loop
    blob = await new Promise<Blob | null>((resolver) => {
      tela.toBlob(resolver, 'image/jpeg', qualidade);
    });
    if (blob && blob.size <= ALVO_BYTES) break;
  }
  if (!blob) throw new Error('Este navegador não conseguiu gerar o JPEG.');
  if (blob.size > TETO_BYTES) {
    throw new Error('A foto ficou grande demais mesmo comprimida — afaste um pouco e tire de novo.');
  }

  const dataUrl = await new Promise<string>((resolver, rejeitar) => {
    const leitor = new FileReader();
    leitor.onload = () => resolver(String(leitor.result));
    leitor.onerror = () => rejeitar(new Error('Não deu para preparar o envio da foto.'));
    leitor.readAsDataURL(blob!);
  });

  return {
    base64: dataUrl.slice(dataUrl.indexOf(',') + 1),
    largura,
    altura,
    bytes: blob.size,
    previa: dataUrl,
  };
}
